import { AlertTriangle, CheckCircle2, Info, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { HintResult } from '@/types';

/**
 * The hints are the product — the rule engine's answer to "what's wrong here?".
 * Sorted hardest-first so the worst news is never below the fold.
 */

const ORDER: Record<string, number> = { critical: 0, warning: 1, info: 2 };

const SEVERITY = {
  critical: {
    icon: ShieldAlert,
    cls: 'border-l-crit bg-crit-bg',
    text: 'text-crit',
    label: 'critical',
  },
  warning: {
    icon: AlertTriangle,
    cls: 'border-l-warn bg-warn-bg',
    text: 'text-warn',
    label: 'warning',
  },
  info: { icon: Info, cls: 'border-l-info bg-info-bg', text: 'text-info', label: 'info' },
} as const;

export function HintsPanel({ hints }: { hints: HintResult[] }) {
  const sorted = [...hints].sort(
    (a, b) => (ORDER[a.severity] ?? 9) - (ORDER[b.severity] ?? 9),
  );
  const broken = sorted.filter((h) => h.error);
  const good = sorted.filter((h) => !h.error);

  if (!hints.length) return <NoHints />;

  return (
    <section className="border-b border-hairline">
      <div className="space-y-px">
        {good.map((h) => (
          <Hint key={h.ruleId} hint={h} />
        ))}
        {broken.map((h) => (
          <BrokenRule key={h.ruleId} hint={h} />
        ))}
      </div>
    </section>
  );
}

function Hint({ hint }: { hint: HintResult }) {
  const meta = SEVERITY[hint.severity] ?? SEVERITY.info;
  const Icon = meta.icon;

  return (
    <div className={cn('flex gap-3 border-l-2 px-4 py-2.5', meta.cls)}>
      <Icon size={14} className={cn('mt-[2px] shrink-0', meta.text)} />
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex items-baseline gap-2">
          <span
            className={cn(
              'font-condensed text-[10px] font-semibold uppercase tracking-wider',
              meta.text,
            )}
          >
            {meta.label}
          </span>
          <span className="font-sans text-[12px] font-medium text-text">{hint.name}</span>
        </div>
        {/* Rendered by LiquidJS on the backend — plain text, not HTML. */}
        <p className="text-[12.5px] leading-[1.55] text-text">{hint.message}</p>
      </div>
      <code className="shrink-0 self-start font-mono text-[10px] text-text-faint">
        {hint.ruleId}
      </code>
    </div>
  );
}

/**
 * A rule whose expression or template failed. Surfaced rather than hidden: a
 * silently broken rule is a rule that isn't protecting anyone.
 */
function BrokenRule({ hint }: { hint: HintResult }) {
  return (
    <div className="flex gap-3 border-l-2 border-l-hairline-strong bg-surface-sunken px-4 py-2">
      <AlertTriangle size={14} className="mt-[2px] shrink-0 text-text-faint" />
      <div className="min-w-0 flex-1">
        <div className="font-sans text-[12px] text-text-dim">
          Rule <code className="font-mono text-text">{hint.ruleId}</code> could not be
          evaluated
        </div>
        <p className="font-mono text-[11px] leading-[1.5] text-text-faint">{hint.error}</p>
      </div>
    </div>
  );
}

function NoHints() {
  return (
    <section className="flex items-center gap-2.5 border-b border-hairline bg-ok-bg/40 px-4 py-2.5">
      <CheckCircle2 size={14} className="shrink-0 text-ok" />
      <div>
        <span className="font-sans text-[12.5px] text-text">No hints matched this bet.</span>{' '}
        <span className="text-[12px] text-text-dim">
          Every configured rule ran and none of them fired — nothing known is wrong here.
        </span>
      </div>
    </section>
  );
}
